import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { applicationStore } from "../../store/ApplicationStore";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import {
  getInformationWithAidCode,
  postFingerPrintInformation,
} from "src/services/functionServices";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import LoadingComponent from "src/components/ui/LoadingComponent";
import CheckLink from "../check-link";
import logger from "src/utils/logger";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import {
  fetchForIdVerification,
  fetchForLiveness,
} from "src/services/fetchModelsServices";
import { NodeTypes } from "src/enums/NodeTypes";
import VerifyCard from "./components/VerifyCard";
import WebSocketService from "src/classes/WebSocketService";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { useTranslation } from "react-i18next";
import { identifySmartlook } from "src/utils/smartlook";
import { logAndroidDeviceInfo } from "src/utils/deviceDetector";
import { toJS } from "mobx";

async function waitForThemePaintIfNeeded(store) {
  if (!store.clientBranding) {
    return;
  }
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
}

const GettingStarted = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [searchParams] = useSearchParams();
  const {
    data: fingerprintData,
    getData: getFingerprint
  } = useVisitorData(
    { extendedResult: true },
    { immediate: false }
  );
  const params = Object.fromEntries([...searchParams]);
  const code = params.code;
  const state = params.state;
  const aid = params.aid || state;
  const returnUrl = params.returnUrl;
  const edd = params.edd;
  const periodic = params.periodic;
  const ref = params.ref;

  const aidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
  logger.log(aid);
  logger.log("edd", edd);
  logger.log("periodic", periodic);
  logger.log(ref);

  const handleVerifyClick = async () => {
    if (applicationStore.isEddForm || applicationStore.isPeriodicForm) {
      navigate("/next", { replace: true });
      return
    }
    const step = applicationStore.workflowNextServiceResponse?.step;
    const country = applicationStore.workflowNextServiceResponse?.country;

    if (!step) {
      return;
    }

    if (applicationStore.workflowNextServiceResponse.verification && country) {
      fetchForIdVerification(country.toLowerCase());
    }

    navigate(country ? "/next" : "/select-country", { replace: true });
  };

  const [verifyCardProps, setVerifyCardProps] = useState({
    title: t('gettingStarted.title'),
    description: t('gettingStarted.description'),
    buttonText: t('gettingStarted.begin'),
    onButtonClick: handleVerifyClick,
  });

  useEffect(() => {
    const initializeFlow = async () => {
      try {
        logAndroidDeviceInfo();

        setLoading(true);
        setError(false);
        if (!ref && !(code && state)) {
          logger.log("reset store");
          applicationStore.resetStore()
          localStorage.removeItem("ApplicationStore")
        } else {
          const storeFormLocalStorege = localStorage.getItem("ApplicationStore")
          logger.log(storeFormLocalStorege.workflowNextServiceResponse);
          const parsedData = JSON.parse(storeFormLocalStorege)
          applicationStore.setWorkflowNextServiceResponse(parsedData.workflowNextServiceResponse)
        }
        applicationStore.setEidCode(code ?? "");
        const isValidAid = aidRegex.test(aid);
        if (!isValidAid) {
          setError(true);
          return toast.warning(t('errors.linkInvalid'));
        }
        if (returnUrl) {
          sessionStorage.setItem("returnUrl", returnUrl);
        }
        if (edd === "true" && periodic === "true") {
          setError(true);
          return toast.warning(t('errors.linkInvalid'));
        }
        const response = await getInformationWithAidCode({ aid, edd, periodic });
        logger.log(response);

        const sessionThemeSettings =
          response.data?.settings && typeof response.data.settings === "object"
            ? response.data.settings
            : null;

        const access_token = edd === "true" ? response.data.edd.token : periodic === "true" ? response.data.token : response.data.access_token
        logger.log(access_token);
        if (access_token == null)
          throw new Error("Aid Code");

        applicationStore.applyAidAuthBootstrap({
          sessionThemeSettings,
          access_token,
          aidCode: aid,
        });

        const decodedToken = jwtDecode(access_token);

        if (decodedToken.exp * 1000 < Date.now())
          return navigate("/link-expired", { replace: true });

        identifySmartlook(decodedToken, aid);

        if (edd === "true") {
          runInAction(() => {
            applicationStore.setIsEddForm(true);
            applicationStore.setEddServiceResponse(response.data);
            applicationStore.setType("EDD");
          });
          await waitForThemePaintIfNeeded(applicationStore);
          setLoading(false);
          return
        }
        if (periodic === "true") {
          runInAction(() => {
            applicationStore.setIsPeriodicForm(true);
            applicationStore.setPeriodicServiceResponse(response.data);
            applicationStore.setType("PERIODIC");
          });
          await waitForThemePaintIfNeeded(applicationStore);
          setLoading(false);
          return
        }

        runInAction(() => {
          applicationStore.setClientId(decodedToken.client_id);
          applicationStore.setWorkflowId(decodedToken.workflow_id);
          applicationStore.setProject(decodedToken.project_id);
          applicationStore.setType(decodedToken.type);
        });

        try {
          const fpResult = await getFingerprint({
            linkedId: decodedToken.client_id
          });
          logger.log('Fingerprint with linkedId:', fpResult);
        } catch (fpError) {
          logger.error('Fingerprint error:', fpError);
        }
        if (
          code &&
          state
        ) {
          console.log("code", code);
          console.log("state", state);
          console.log("applicationStore.workflowNextServiceResponse", toJS(applicationStore.workflowNextServiceResponse));
          const response = await workflowStepIterator({
            access_token: applicationStore.access_token,
            node_id: applicationStore.workflowNextServiceResponse.node_id,
            body: {
              code: code,
              state: state
            },
            action_index: 1,
            constextOfStep: {
              step: NodeTypes.EID_VERIFICATION,
              retry: applicationStore.workflowNextServiceResponse.retry,
            },
          });
          if (response.data?.service_results[0].status !== 200) {
            const rawMessage = response.data?.service_results[0]?.message;
            const parsedMessage = parseServiceErrorMessage(rawMessage);
            throw new Error(parsedMessage);
          }
          const serviceData = response.data?.service_results[0].data;
          const authorizationUrl = serviceData?.authorization_url;
          if (authorizationUrl) {
            applicationStore.setWorkflowNextServiceResponse({ ...response.data, index: 1 });
            localStorage.setItem("ApplicationStore", JSON.stringify(applicationStore));
            setLoading(false);
            window.location.replace(authorizationUrl);
            return;
          }
          applicationStore.setWorkflowNextServiceResponse(response.data);
          setLoading(false);
          navigate("/next", { replace: true });
          return;
        }

        if (applicationStore.workflowNextServiceResponse.step === NodeTypes.BANK_VERIFICATION &&
          applicationStore.workflowNextServiceResponse.index === 3 &&
          ref
        ) {
          const response = await workflowStepIterator({
            access_token: applicationStore.access_token,
            node_id: applicationStore.workflowNextServiceResponse.node_id,
            body: {
              plato: "plato",
            },
            action_index: 3,
            constextOfStep: { step: NodeTypes.VERIFICATION },
          });
          if (response.data?.service_results[0].status !== 200) {
            const rawMessage = response.data?.service_results[0]?.message;
            const parsedMessage = parseServiceErrorMessage(rawMessage);
            throw new Error(parsedMessage);
          }
          applicationStore.setWorkflowNextServiceResponse(response.data);
          if (
            !response.data?.service_results[0].data.completed ||
            !response.data?.service_results[0].data.validated
          ) {
            await waitForThemePaintIfNeeded(applicationStore);
            setLoading(false);
            navigate("/next", { replace: true });
            return;
          }
        } else {
          const response = await workflowStepIterator({
            access_token: applicationStore.access_token,
          });
          applicationStore.setWorkflowNextServiceResponse(response.data);
        }

        const webSocketService = WebSocketService.getInstance();
        webSocketService.initialize(
          'wss://' + window.location.host + '/websocket',
          applicationStore.access_token,
          applicationStore,
          navigate
        );

        if (applicationStore.workflowNextServiceResponse?.country) {
          applicationStore.setCountry(applicationStore.workflowNextServiceResponse.country.toLowerCase());
          setVerifyCardProps(getUpdatedVerifyCardProps());
        }
        if (applicationStore.workflowNextServiceResponse?.liveness) {
          fetchForLiveness();
        }
        await waitForThemePaintIfNeeded(applicationStore);
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error(`${error.response?.data?.detail?.message}`);
          return navigate("/link-expired");
        }
        applicationStore.setApplicationErrorObj({
          header: t('errors.loadingFlowHeader'),
          description: t('errors.loadingFlowDescription'),
          buttonList: [
            {
              label: t('actions.retry'),
              variant: "primaryWithoutIcon",
              onClick: async () => {
                try {
                  const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                  });
                  applicationStore.setWorkflowNextServiceResponse(response.data);
                  navigate("/next", { replace: true });
                } catch (error) {
                  toast.error(error?.message || t('errors.generic'));
                }
              }
            }
          ]
        });
        navigate("/error-page", { replace: true });
        return
      }
    };
    initializeFlow();
  }, [searchParams, t]);

  useEffect(() => {
    const sendFingerPrint = async () => {
      if (!loading && applicationStore?.access_token && fingerprintData?.requestId) {
        try {
          await postFingerPrintInformation(
            fingerprintData.requestId,
            applicationStore.access_token
          );
          logger.log('Fingerprint sent successfully');
        } catch (error) {
          logger.error('Fingerprint send error:', error);
        }
      }
    };
    sendFingerPrint();
  }, [fingerprintData, loading]);

  const getUpdatedVerifyCardProps = () => {
    return {
      title: t('gettingStarted.proceedTitle'),
      description: t('gettingStarted.proceedDescription'),
      buttonText: t('gettingStarted.continue'),
      onButtonClick: handleVerifyClick,
    };
  };

  // When language changes, refresh visible texts
  useEffect(() => {
    if (applicationStore.workflowNextServiceResponse?.country) {
      setVerifyCardProps(getUpdatedVerifyCardProps());
    } else {
      setVerifyCardProps({
        title: t('gettingStarted.title'),
        description: t('gettingStarted.description'),
        buttonText: t('gettingStarted.begin'),
        onButtonClick: handleVerifyClick,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <div className="custom-container">
      {error ? (
        <CheckLink />
      ) : loading ? (
        <LoadingComponent />
      ) : (
        <VerifyCard {...verifyCardProps} />
      )}
    </div>
  );
});

export default GettingStarted;
